import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { IMPERSONATION_KEY, readImpersonation, stopImpersonation } from "../api/impersonation";
import { socketClient } from "../api/socket";
import { logout as apiLogout, verifyGoogleIdToken } from "../api/auth";
import type { UserData } from "../types";
import { GUEST } from "../types";

interface AuthContextValue {
  user: UserData;
  isAuthenticated: boolean;
  signInWithGoogle: (idToken: string) => Promise<UserData>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserData>(socketClient.userdata);

  // The websocket handshake is the canonical source of truth for the
  // authenticated user (see backend/handlers/websocket_handler.py). When
  // the socket reconnects with a new auth cookie the `connected` frame
  // contains the fresh user data, so we wire ourselves to that.
  useEffect(() => {
    const off = socketClient.on<UserData>("connected", (msg) => {
      const identity = msg.content;
      const saved = readImpersonation();
      if (identity?.impersonation && saved?.session_id !== identity.impersonation.session_id) {
        localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
          username: identity.username, ...identity.impersonation,
        }));
        window.location.reload();
        return;
      }
      if (saved && identity?.id != null && identity.id !== -1 && !identity.impersonation) {
        localStorage.removeItem(IMPERSONATION_KEY);
        window.location.reload();
        return;
      }
      setUser({ ...GUEST, ...(identity || {}), initialized: true });
    });
    socketClient.connect().then((data) => setUser(data));
    return off;
  }, []);

  const signInWithGoogle = useCallback(async (idToken: string) => {
    if (readImpersonation()) throw new Error("Bitte zuerst zum Admin zurückkehren.");
    const data = await verifyGoogleIdToken(idToken);
    // The verify endpoint sets fresh auth cookies; reconnect the WS so the
    // handshake picks up the new user identity.
    const refreshed = await socketClient.reconnect();
    setUser(refreshed);
    return { ...refreshed, tos: data.tos } as UserData;
  }, []);

  const signOut = useCallback(async () => {
    if (readImpersonation() || socketClient.userdata.impersonation) {
      await stopImpersonation();
      return;
    }
    try {
      await apiLogout();
      localStorage.removeItem(IMPERSONATION_KEY);
    } catch (err) {
      console.error("Logout failed", err);
    }
    socketClient.send("logout", "");
    setUser({ ...GUEST });
    await socketClient.reconnect();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      // Guest WS clients use username "guest" and id -1. The backend used to send
      // only `username` on `connected`, leaving id at -1 from GUEST merge — treat
      // any non-guest username as signed-in. Prefer id !== -1 when present.
      isAuthenticated:
        !!user &&
        user.initialized &&
        ((typeof user.id === "number" && user.id !== -1) ||
          (!!user.username && user.username !== "guest")),
      signInWithGoogle,
      signOut,
    }),
    [user, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
